import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';

type BufferContextType = {
  registerApplyFunction: (fn: () => void) => void;
  unregisterApplyFunction: (fn: () => void) => void;
  applyAllChanges: () => void;
};

const BufferContext = createContext<BufferContextType | undefined>(undefined);

export function BufferProvider({ children }: Readonly<PropsWithChildren<{}>>): JSX.Element {
  const applyFunctionsRef = useRef<Set<() => void>>(new Set());

  const registerApplyFunction = useCallback((fn: () => void) => {
    applyFunctionsRef.current.add(fn);
  }, []);

  const unregisterApplyFunction = useCallback((fn: () => void) => {
    applyFunctionsRef.current.delete(fn);
  }, []);

  const applyAllChanges = useCallback(() => {
    applyFunctionsRef.current.forEach((fn) => fn());
  }, []);

  const value = useMemo(() => ({ registerApplyFunction, unregisterApplyFunction, applyAllChanges }), [
    registerApplyFunction,
    unregisterApplyFunction,
    applyAllChanges,
  ]);

  return <BufferContext.Provider value={value}>{children}</BufferContext.Provider>;
}

export function useBufferContext(): BufferContextType {
  const context = useContext(BufferContext);
  if (!context) {
    throw new Error('useBufferContext must be used within BufferProvider');
  }
  return context;
}
